import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { PrivacyPolicyModal } from "sipli-fleet-core/components";

import { ROUTE_CONSTANTS } from "src/routes/routeConstant";
import {
  acceptPrivacyPolicy,
  extractFirstParagraph,
  getPrivacyPolicyAcceptanceStatus,
} from "src/services/privacyPolicyService";
import { getFromLocal } from "src/util/cache.service";
import { LOCAL_KEYS } from "src/util/util.constant";

interface UserDetail {
  id?: string | number;
}

const ACCEPTANCE_QUERY_KEY = "PRIVACY_POLICY_ACCEPTANCE_STATUS";

interface PrivacyPolicyGateProps {
  onBlockingChange: (blocking: boolean) => void;
}

const PrivacyPolicyGate = ({ onBlockingChange }: PrivacyPolicyGateProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userId = getFromLocal<UserDetail>(LOCAL_KEYS.USER_DETAIL)?.id;
  const language = i18n.resolvedLanguage || i18n.language;
  const queryKey = [ACCEPTANCE_QUERY_KEY, userId, language];
  const { data: status, isPending: isStatusPending } = useQuery({
    queryKey,
    queryFn: () => getPrivacyPolicyAcceptanceStatus(userId!),
    enabled: userId != null,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const policy = status?.policyToAccept;
  const isBlocking = userId != null && (isStatusPending || Boolean(status?.acceptanceRequired));

  useEffect(() => {
    onBlockingChange(isBlocking);
  }, [isBlocking, onBlockingChange]);

  const { mutate: accept, isPending: isAccepting } = useMutation({
    mutationFn: () => acceptPrivacyPolicy(userId!, policy!.id!),
    onSuccess: () => {
      queryClient.setQueryData(queryKey, {
        ...status,
        acceptanceRequired: false,
      });
    },
  });

  const policyDateValue = policy?.publishedAt || policy?.effectiveDate || policy?.updatedAt;
  const policyDate = policyDateValue && dayjs(policyDateValue).isValid()
    ? dayjs(policyDateValue).format("DD/MM/YYYY")
    : undefined;

  return (
    <PrivacyPolicyModal
      open={Boolean(status?.acceptanceRequired && policy?.id != null)}
      appName="CRM"
      isFirstAccess={status?.lastAcceptedPolicyId == null}
      firstParagraph={extractFirstParagraph(policy?.content)}
      policyDate={policyDate}
      accepting={isAccepting}
      onOpenPolicy={() =>
        navigate(ROUTE_CONSTANTS.PRIVACY_POLICY, {
          state: { returnTo: `${location.pathname}${location.search}` },
        })
      }
      onAccept={() => accept()}
    />
  );
};

export default PrivacyPolicyGate;
